// src/components/Assets/AssetPermissions/PermissionCard.js
// Permission request card with approve/deny actions

import React from "react";
import { useTranslation } from "react-i18next";
import { FaCalendar, FaCheckCircle, FaTimesCircle, FaTools, FaTractor } from "react-icons/fa";
import PropTypes from "prop-types";
import PermissionStatusBadge from "./PermissionStatusBadge";
import "./PermissionStyles.css";

const PermissionCard = ({ permission, onApprove, onDeny, showActions = false }) => {
  const [t, { language }] = useTranslation();

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const isPending = permission.status === "pending";

  // Get farm name - Backend returns farm.name instead of farm.farmName
  const farmName = permission.farm?.name || permission.farm?.farmName || permission.farmName;

  return (
    <div className="permission-card">
      {/* Card Header */}
      <div className="permission-header">
        <div className="permission-asset-info">
          <div className="asset-icon-small">
            <FaTools />
          </div>
          <div>
            <h3 className="permission-asset-name">
              {permission.asset?.name || t("Unknown Asset")}
            </h3>
            <PermissionStatusBadge status={permission.status} />
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="permission-body">
        {/* Farm Information - Support multiple possible data structures */}
        {farmName && (
          <div className="permission-info-row">
            <div className="info-icon">
              <FaTractor />
            </div>
            <div className="info-content">
              <span className="info-label">{t("Farm")}:</span>
              <span className="info-value">{farmName}</span>
            </div>
          </div>
        )}

        {/* Date Range */}
        <div className="permission-info-row">
          <div className="info-icon">
            <FaCalendar />
          </div>
          <div className="info-content date-range">
            <span>
              <strong>{t("From")}:</strong> {formatDate(permission.requestDate)}
            </span>
            <span className="date-separator">→</span>
            <span>
              <strong>{t("To")}:</strong> {formatDate(permission.requestEndDate)}
            </span>
          </div>
        </div>

        {/* Notes */}
        {permission.notes && (
          <div className="permission-notes">
            <strong>{t("Notes")}:</strong>
            <p>{permission.notes}</p>
          </div>
        )}
      </div>

      {/* Card Actions (Admin only, Pending only) */}
      {showActions && isPending && (
        <div className="permission-actions">
          <button
            className="button-success button-success-sm"
            onClick={() => onApprove(permission.id)}
          >
            <FaCheckCircle /> {t("Approve")}
          </button>
          <button
            className="button-danger button-danger-sm"
            onClick={() => onDeny(permission.id)}
          >
            <FaTimesCircle /> {t("Deny")}
          </button>
        </div>
      )}
    </div>
  );
};

// PropTypes validation
PermissionCard.propTypes = {
  permission: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    asset: PropTypes.shape({
      name: PropTypes.string
    }),
    farm: PropTypes.shape({
      farmName: PropTypes.string,
      name: PropTypes.string
    }),
    farmName: PropTypes.string, // Fallback if farm name is at root level
    requestDate: PropTypes.number,
    requestEndDate: PropTypes.number,
    notes: PropTypes.string
  }).isRequired,
  onApprove: PropTypes.func,
  onDeny: PropTypes.func,
  showActions: PropTypes.bool
};

// Default props
PermissionCard.defaultProps = {
  showActions: false,
  onApprove: () => {},
  onDeny: () => {}
};

export default PermissionCard;
